import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { ConnectionStatus as WsConnectionStatus, WsStats } from '../services/visitorWebSocketClient';
import { useConnectionStatus, ConnectionState } from '../contexts/ConnectionStatusContext';
import { useSystemHealth, SystemHealthStatus } from '../contexts/SystemHealthContext';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { cn } from '../utils/cn';

interface ConnectionStatusIndicatorProps {
  className?: string;
  showText?: boolean;
  size?: 'sm' | 'md';
  eventCount?: number;
  lastEventDescription?: string;
  lastEventTime?: string;
  wsConnectionStatus?: WsConnectionStatus;
  getWsStats?: () => WsStats | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelative(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 5) return 'agora';
  if (secs < 60) return `há ${secs}s`;
  return `há ${Math.floor(secs / 60)}min`;
}

function formatUptime(since: Date): string {
  const secs = Math.floor((Date.now() - since.getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}h ${rem}m`;
}

// Combined status type that considers both SSE connection and system health
type CombinedStatus = 'online' | 'degraded' | 'offline' | 'connecting' | 'reconnecting';

// UI labels in Portuguese (Brazil) - modern Brazilian Portuguese
const COMBINED_STATUS_CONFIG: Record<CombinedStatus, {
  label: string;
  tooltip: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
  textColor: string;
  animate: boolean;
}> = {
  online: {
    label: 'Online',
    tooltip: 'Sistema funcionando normalmente',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    dotColor: 'bg-emerald-500',
    textColor: 'text-emerald-700',
    animate: true,
  },
  degraded: {
    label: 'Instável',
    tooltip: 'Sistema parcialmente indisponível',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    dotColor: 'bg-amber-500',
    textColor: 'text-amber-700',
    animate: true,
  },
  offline: {
    label: 'Offline',
    tooltip: 'Sistema indisponível',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    dotColor: 'bg-red-500',
    textColor: 'text-red-700',
    animate: false,
  },
  connecting: {
    label: 'Conectando...',
    tooltip: 'Estabelecendo conexão com o servidor',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    dotColor: 'bg-yellow-500',
    textColor: 'text-yellow-700',
    animate: true,
  },
  reconnecting: {
    label: 'Reconectando...',
    tooltip: 'Conexão perdida, tentando reconectar',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-500',
    textColor: 'text-orange-700',
    animate: true,
  },
};

// Legacy config for backwards compatibility
const STATUS_CONFIG: Record<ConnectionState, {
  label: string;
  tooltip: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
  textColor: string;
  animate: boolean;
}> = {
  connected: COMBINED_STATUS_CONFIG.online,
  connecting: COMBINED_STATUS_CONFIG.connecting,
  reconnecting: COMBINED_STATUS_CONFIG.reconnecting,
  disconnected: COMBINED_STATUS_CONFIG.offline,
};

// Helper function to combine SSE connection status with system health
function getCombinedStatus(
  connectionState: ConnectionState,
  systemHealth: SystemHealthStatus
): CombinedStatus {
  // Services offline overrides everything
  if (systemHealth === 'offline') return 'offline';

  // If all services are online, SSE state is secondary — being "connecting" is fine
  // (e.g. global/admin mode where no SSE stream is active)
  if (systemHealth === 'online') {
    if (connectionState === 'reconnecting') return 'reconnecting';
    if (connectionState === 'disconnected') return 'offline';
    return 'online'; // 'connected' or 'connecting' with healthy services = online
  }

  // SSE actively reconnecting
  if (connectionState === 'reconnecting') return 'reconnecting';
  // SSE disconnected
  if (connectionState === 'disconnected') return 'offline';
  // SSE still connecting
  if (connectionState === 'connecting') return 'connecting';

  // If some services are unhealthy, show degraded
  if (systemHealth === 'degraded') return 'degraded';

  // Still checking health
  if (systemHealth === 'checking') return 'connecting';

  return 'online';
}

export const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  className,
  showText = true,
  size = 'md',
}) => {
  const { status } = useConnectionStatus();
  const { overallStatus: systemHealth, unhealthyCount } = useSystemHealth();

  // Get combined status from SSE connection and system health
  const combinedStatus = getCombinedStatus(status.state, systemHealth);
  const config = COMBINED_STATUS_CONFIG[combinedStatus];

  const [showOnlineText, setShowOnlineText] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prevStatusRef = useRef<CombinedStatus>(combinedStatus);

  // When online, show text for 3 seconds, then fade to just dot
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (combinedStatus === 'online') {
      // If we just became online (from another state), show text for 3 seconds
      if (prevStatusRef.current !== 'online') {
        setShowOnlineText(true);
        timerRef.current = setTimeout(() => {
          setShowOnlineText(false);
        }, 3000);
      }
    } else {
      // Not online, reset to show text when we become online
      setShowOnlineText(true);
    }

    prevStatusRef.current = combinedStatus;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [combinedStatus]);

  // Show text for non-online states, or for online state only during first 3 seconds
  const shouldShowText = showText && (combinedStatus !== 'online' || showOnlineText);

  const sizeClasses = {
    sm: {
      container: 'px-1.5 py-0.5',
      dot: 'w-1.5 h-1.5',
      text: 'text-[10px]',
    },
    md: {
      container: 'px-2 py-1',
      dot: 'w-2 h-2',
      text: 'text-xs',
    },
  };

  const sizes = sizeClasses[size];

  // Build tooltip with additional details
  let tooltipText = config.tooltip;
  if (status.state === 'reconnecting' && status.reconnectAttempts > 0) {
    tooltipText = `${config.tooltip} (tentativa ${status.reconnectAttempts})`;
  } else if (combinedStatus === 'degraded' && unhealthyCount > 0) {
    tooltipText = `${unhealthyCount} serviço(s) com problema`;
  } else if (status.lastHeartbeat && combinedStatus === 'online') {
    const secondsAgo = Math.floor((Date.now() - status.lastHeartbeat.getTime()) / 1000);
    if (secondsAgo < 60) {
      tooltipText = `${config.tooltip} (último sinal há ${secondsAgo}s)`;
    }
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-md border transition-all duration-300',
        shouldShowText ? 'gap-1.5' : 'gap-0',
        sizes.container,
        config.bgColor,
        config.borderColor,
        className
      )}
      title={tooltipText}
    >
      <div
        className={cn(
          'rounded-full flex-shrink-0',
          sizes.dot,
          config.dotColor,
          config.animate && 'animate-pulse'
        )}
      />
      <span
        className={cn(
          'font-medium transition-all duration-300 overflow-hidden whitespace-nowrap',
          sizes.text,
          config.textColor,
          shouldShowText ? 'max-w-[100px] opacity-100' : 'max-w-0 opacity-0'
        )}
      >
        {config.label}
      </span>
    </div>
  );
};

// Dark theme version for operational mode
export const ConnectionStatusIndicatorDark: React.FC<ConnectionStatusIndicatorProps> = ({
  className,
  showText = true,
  size = 'sm',
}) => {
  const { status } = useConnectionStatus();

  const darkConfig: Record<ConnectionState, {
    label: string;
    bgColor: string;
    borderColor: string;
    dotColor: string;
    textColor: string;
    animate: boolean;
  }> = {
    connected: {
      label: 'Online',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
      dotColor: 'bg-green-500',
      textColor: 'text-green-400',
      animate: true,
    },
    connecting: {
      label: 'Conectando...',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/20',
      dotColor: 'bg-yellow-500',
      textColor: 'text-yellow-400',
      animate: true,
    },
    reconnecting: {
      label: 'Reconectando...',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
      dotColor: 'bg-orange-500',
      textColor: 'text-orange-400',
      animate: true,
    },
    disconnected: {
      label: 'Offline',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
      dotColor: 'bg-red-500',
      textColor: 'text-red-400',
      animate: false,
    },
  };

  const config = darkConfig[status.state];

  const sizeClasses = {
    sm: {
      container: 'px-2 py-0.5',
      dot: 'w-1.5 h-1.5',
      text: 'text-[10px]',
    },
    md: {
      container: 'px-2.5 py-1',
      dot: 'w-2 h-2',
      text: 'text-xs',
    },
  };

  const sizes = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-lg border',
        sizes.container,
        config.bgColor,
        config.borderColor,
        className
      )}
    >
      <div
        className={cn(
          'rounded-full',
          sizes.dot,
          config.dotColor,
          config.animate && 'animate-pulse'
        )}
      />
      {showText && (
        <span className={cn('font-medium', sizes.text, config.textColor)}>
          {config.label}
        </span>
      )}
    </div>
  );
};

// Dark theme version with hover dropdown for operational mode
export const ConnectionStatusIndicatorDarkWithDropdown: React.FC<ConnectionStatusIndicatorProps> = ({
  className,
  showText = true,
  size = 'sm',
  eventCount,
  lastEventDescription,
  lastEventTime,
  wsConnectionStatus,
  getWsStats,
}) => {
  const { status } = useConnectionStatus();
  const { overallStatus: systemHealth, services, unhealthyCount, isChecking: isCheckingHealth, checkHealth } = useSystemHealth();
  const { data: systemStats, isLoading: isLoadingStats, refreshIfStale } = useSystemStatus(30000);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
  const [wsStats, setWsStats] = useState<WsStats | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Refresh WS stats every 500ms while the dropdown is open
  useEffect(() => {
    if (!isHovered || !getWsStats) return;
    setWsStats(getWsStats());
    const interval = setInterval(() => setWsStats(getWsStats()), 500);
    return () => clearInterval(interval);
  }, [isHovered, getWsStats]);

  // Get combined status
  const combinedStatus = getCombinedStatus(status.state, systemHealth);

  const darkConfig: Record<CombinedStatus, {
    label: string;
    bgColor: string;
    borderColor: string;
    dotColor: string;
    textColor: string;
    animate: boolean;
  }> = {
    online: {
      label: 'Conectado',
      bgColor: 'bg-green-500/15',
      borderColor: 'border-green-500/30',
      dotColor: 'bg-green-400',
      textColor: 'text-green-400',
      animate: true,
    },
    degraded: {
      label: 'Instável',
      bgColor: 'bg-amber-500/15',
      borderColor: 'border-amber-500/30',
      dotColor: 'bg-amber-400',
      textColor: 'text-amber-400',
      animate: true,
    },
    offline: {
      label: 'Offline',
      bgColor: 'bg-red-500/15',
      borderColor: 'border-red-500/30',
      dotColor: 'bg-red-400',
      textColor: 'text-red-400',
      animate: false,
    },
    connecting: {
      label: 'Conectando...',
      bgColor: 'bg-yellow-500/15',
      borderColor: 'border-yellow-500/30',
      dotColor: 'bg-yellow-400',
      textColor: 'text-yellow-400',
      animate: true,
    },
    reconnecting: {
      label: 'Reconectando...',
      bgColor: 'bg-orange-500/15',
      borderColor: 'border-orange-500/30',
      dotColor: 'bg-orange-400',
      textColor: 'text-orange-400',
      animate: true,
    },
  };

  const config = darkConfig[combinedStatus];

  const sizeClasses = {
    sm: {
      container: 'px-2.5 py-1',
      dot: 'w-2 h-2',
      text: 'text-[11px]',
    },
    md: {
      container: 'px-3 py-1.5',
      dot: 'w-2.5 h-2.5',
      text: 'text-xs',
    },
  };

  const sizes = sizeClasses[size];

  // SSE connection state labels
  const SSE_STATE_LABELS: Record<string, { label: string; color: string }> = {
    connected: { label: 'Conectado', color: 'text-green-400' },
    connecting: { label: 'Conectando...', color: 'text-yellow-400' },
    reconnecting: { label: 'Reconectando...', color: 'text-orange-400' },
    disconnected: { label: 'Desconectado', color: 'text-red-400' },
  };

  const sseStateLabel = SSE_STATE_LABELS[status.state] || SSE_STATE_LABELS.disconnected;

  // Format last heartbeat relative time
  const getHeartbeatText = () => {
    if (!status.lastHeartbeat) return null;
    const secondsAgo = Math.floor((Date.now() - status.lastHeartbeat.getTime()) / 1000);
    if (secondsAgo < 60) return `há ${secondsAgo}s`;
    return `há ${Math.floor(secondsAgo / 60)}min`;
  };

  const heartbeatText = getHeartbeatText();

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setIsHovered(true);
    refreshIfStale();
  };

  return (
    <div
      ref={triggerRef}
      className="relative electron-no-drag"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          'flex items-center gap-1.5 rounded-full border cursor-pointer transition-all duration-150',
          'hover:brightness-125',
          sizes.container,
          config.bgColor,
          config.borderColor,
          className
        )}
      >
        <div
          className={cn(
            'rounded-full flex-shrink-0',
            sizes.dot,
            config.dotColor,
            config.animate && 'animate-pulse'
          )}
        />
        {showText && (
          <span className={cn('font-semibold tracking-wide', sizes.text, config.textColor)}>
            {config.label}
          </span>
        )}
      </div>

      {/* Hover dropdown — uses fixed positioning to escape overflow:hidden parents */}
      {isHovered && dropdownPos && (
        <div
          className="fixed w-64 bg-gray-950 rounded-lg shadow-2xl border border-gray-800 z-[9999] overflow-hidden"
          style={{ top: dropdownPos.top, right: dropdownPos.right }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-gray-800 bg-black">
            <div className="flex items-center gap-2">
              <div className={cn('w-2 h-2 rounded-full flex-shrink-0', config.dotColor, config.animate && 'animate-pulse')} />
              <span className={cn('text-xs font-semibold', config.textColor)}>
                {combinedStatus === 'online' ? 'Sistema funcionando' :
                 combinedStatus === 'degraded' ? `${unhealthyCount} serviço(s) com problema` :
                 combinedStatus === 'connecting' ? 'Estabelecendo conexão...' :
                 combinedStatus === 'reconnecting' ? `Reconectando (tentativa ${status.reconnectAttempts})` :
                 'Sistema indisponível'}
              </span>
            </div>
          </div>

          {/* Services health */}
          <div className="px-3 py-2 border-b border-gray-800">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Saúde dos Serviços</span>
              <button
                onClick={(e) => { e.stopPropagation(); checkHealth(); }}
                className="text-gray-600 hover:text-gray-400 transition-colors"
                title="Atualizar"
              >
                <RefreshCw className={cn('h-3 w-3', isCheckingHealth && 'animate-spin')} />
              </button>
            </div>
            <div className="space-y-1">
              {services.map((svc) => (
                <div key={svc.name} className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{svc.displayName}</span>
                  <div className="flex items-center gap-1.5">
                    {svc.status === 'healthy' ? (
                      <>
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span className="text-[10px] text-green-400 font-medium">OK</span>
                      </>
                    ) : svc.status === 'unhealthy' ? (
                      <>
                        <XCircle className="h-3 w-3 text-red-500" />
                        <span className="text-[10px] text-red-400 font-medium">Erro</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 text-gray-500 animate-spin" />
                        <span className="text-[10px] text-gray-500">...</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SSE + WS connection status */}
          <div className="px-3 py-2 border-b border-gray-800">
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Conexão em Tempo Real</span>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">Status SSE:</span>
                <span className={cn('text-xs font-semibold', sseStateLabel.color)}>{sseStateLabel.label}</span>
              </div>
              {heartbeatText && status.state === 'connected' && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Último sinal:</span>
                  <span className="text-xs text-gray-300">{heartbeatText}</span>
                </div>
              )}
              {status.reconnectAttempts > 0 && status.state !== 'connected' && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Tentativas:</span>
                  <span className="text-xs text-orange-400 font-medium">{status.reconnectAttempts}</span>
                </div>
              )}
              {status.lastError && status.state !== 'connected' && (
                <p className="text-[10px] text-red-400 mt-1 break-words">{status.lastError}</p>
              )}
              {wsConnectionStatus && (
                <div className="pt-0.5 border-t border-gray-800/60 mt-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">WS Visitantes:</span>
                    <span className={cn(
                      'text-xs font-semibold',
                      wsConnectionStatus === 'connected' ? 'text-blue-400' :
                      wsConnectionStatus === 'connecting' ? 'text-yellow-400' :
                      wsConnectionStatus === 'reconnecting' ? 'text-orange-400' :
                      'text-red-400'
                    )}>
                      {wsConnectionStatus === 'connected' ? 'Conectado' :
                       wsConnectionStatus === 'connecting' ? 'Conectando...' :
                       wsConnectionStatus === 'reconnecting' ? 'Reconectando...' :
                       'Desconectado'}
                    </span>
                  </div>
                  {wsStats && (
                    <>
                      {wsStats.connectedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Uptime:</span>
                          <span className="text-xs text-gray-300">
                            {formatUptime(wsStats.connectedAt)}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">Msgs recebidas:</span>
                        <span className="text-xs font-semibold text-green-400">{wsStats.messagesReceived}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">Msgs enviadas:</span>
                        <span className="text-xs font-semibold text-blue-400">{wsStats.messagesSent}</span>
                      </div>
                      {wsStats.lastReceivedAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Última recebida:</span>
                          <span className="text-xs text-gray-300">{formatRelative(wsStats.lastReceivedAt)}</span>
                        </div>
                      )}
                      {wsStats.lastSentAt && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Última enviada:</span>
                          <span className="text-xs text-gray-300">{formatRelative(wsStats.lastSentAt)}</span>
                        </div>
                      )}
                      {wsStats.reconnectCount > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Reconexões:</span>
                          <span className="text-xs text-orange-400 font-semibold">{wsStats.reconnectCount}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Event counters */}
          {eventCount !== undefined && (
            <div className="px-3 py-2 border-b border-gray-800">
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Eventos Recebidos</span>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Total na sessão:</span>
                  <span className="text-xs font-semibold text-green-400">{eventCount}</span>
                </div>
                {lastEventDescription && (
                  <div className="mt-1">
                    <span className="text-[10px] text-gray-500 block mb-0.5">Último evento:</span>
                    <p className="text-[10px] text-gray-300 leading-tight">{lastEventDescription}</p>
                    {lastEventTime && (
                      <span className="text-[10px] text-gray-600">{lastEventTime}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System stats */}
          <div className="px-3 py-2">
            <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Estatísticas</span>
            {isLoadingStats ? (
              <div className="space-y-1.5">
                <div className="h-3 bg-gray-800 rounded animate-pulse w-full" />
                <div className="h-3 bg-gray-800 rounded animate-pulse w-3/4" />
              </div>
            ) : systemStats ? (
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {systemStats.stats.totalCustomers !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Clientes:</span>
                      <span className="text-[10px] font-semibold text-gray-200">{systemStats.stats.totalCustomers}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Pessoas:</span>
                    <span className="text-[10px] font-semibold text-gray-200">{systemStats.stats.totalPeople}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Zonas:</span>
                    <span className="text-[10px] font-semibold text-gray-200">{systemStats.stats.totalZones}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">Dispositivos:</span>
                    <span className="text-[10px] font-semibold text-gray-200">{systemStats.stats.totalDevices}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-[10px] text-gray-400">Acessos hoje:</span>
                  <span className="text-[10px] font-semibold text-green-400">{systemStats.stats.todayEvents}</span>
                </div>
                {systemStats.devices.total > 0 && (
                  <div className="pt-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-400">
                        {systemStats.devices.online} online / {systemStats.devices.offline} offline
                      </span>
                      <span className="text-[10px] font-semibold text-gray-300">{systemStats.devices.percentage}%</span>
                    </div>
                    <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${systemStats.devices.percentage}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[10px] text-gray-600">Estatísticas indisponíveis</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatusIndicator;
